import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { Dropdown, DropdownProps } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { useSelectFirstAndOnlyOption } from '../../../../../../../hooks'
import { getPackagingItemHierarchyRestrictions } from '../../../../../../../utils/deliveryforecast-despatchadvice'
import { calculateBreakpointWidth } from '../../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../../ui-config'
import { ParentOption, ParentOptions } from '../../../../../utils'
import { FormFieldName, FormValues } from '../../common'
import { BatchesTableData } from '../../common/BatchesTable'
import { PackagingMaterialTable } from './../../common/PackagingMaterialTable'

type Props = {
  visible: boolean
  formik: FormikProps<FormValues>
  isFormFieldValid: (name: FormFieldName, index?: number) => boolean
  getFormErrorMessage: (
    name: FormFieldName,
    index?: number,
  ) => false | JSX.Element
  handleClose: () => void
  isRequestRunning: boolean
  parents: ParentOptions
  despatchAdviceId: string
  itemId: string
}

export const AddView: React.FC<Props> = ({
  visible,
  formik,
  isFormFieldValid,
  getFormErrorMessage,
  handleClose,
  isRequestRunning,
  parents,
  despatchAdviceId,
  itemId,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()

  const hasBatches = useCallback((): boolean => {
    if ((formik.values.batches as []).length > 0) return true
    return false
  }, [formik.values.batches])

  const sumBatches = useCallback((): number => {
    if (!hasBatches()) return 0
    return formik.values.batches.reduce(
      (prev: any, next: any) => prev + next['Quantity'],
      0,
    )
  }, [formik.values.batches, hasBatches])

  useEffect(() => {
    const isSingle = formik.values.label === 'Single'

    if (isSingle) {
      const currentQuantity = formik.values.quantity
      const batchQuantity = sumBatches()

      if (batchQuantity > 0 && currentQuantity !== batchQuantity) {
        formik.setFieldValue('quantity', sumBatches())
      }
    }
  }, [formik, hasBatches, sumBatches])

  // ************************************************************
  // components
  // ************************************************************
  // Label
  const labelLabel = (
    <label
      htmlFor="label"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('label'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'label',
    config.deliveryForecastDespatchAdvice.addPackagingItem.label.options,
    formik,
  )

  const labelComponent = (
    <Dropdown
      id="label"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('label'),
      })}
      value={formik.values.label}
      onChange={event => {
        formik.setFieldValue('label', event.value)
      }}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const labelErrorMsg = <div>{getFormErrorMessage('label')}</div>

  const labelWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.addPackagingItem}
      component={labelComponent}
      label={labelLabel}
      errorMsg={labelErrorMsg}
    />
  )

  // HuNumber
  const huNumberLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('huNumber'),
      })}
      htmlFor="huNumber"
    ></label>
  )

  const huNumberComponent = (
    <InputText
      id="huNumber"
      value={formik.values.huNumber}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('huNumber'),
      })}
    />
  )

  const huNumberErrorMsg = <div>{getFormErrorMessage('huNumber')}</div>

  const huNumberWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.addPackagingItem}
      component={huNumberComponent}
      label={huNumberLabel}
      errorMsg={huNumberErrorMsg}
    />
  )

  // IsOuterPackaging
  const isOuterPackagingLabel = (
    <label
      htmlFor="isOuterPackaging"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('isOuterPackaging'),
      })}
    ></label>
  )

  const hasMaster = !!formik.values.selectMaster

  const isOuterPackagingComponent = (
    <div
      className="flex align-items-center"
      id="isOuterPackaging"
      data-checkbox
    >
      <Checkbox
        id="isOuterPackaging"
        inputId="isOuterPackaging"
        name="isOuterPackaging"
        value="yes"
        onChange={formik.handleChange}
        checked={formik.values.isOuterPackaging || false}
        className={classNames({
          'p-invalid': !isFormFieldValid('isOuterPackaging'),
        })}
        disabled={isRequestRunning || hasMaster}
      />
      <label htmlFor="isOuterPackaging" className="ml-2">
        {t(
          'masterDataManagement.packagingData.table.edit.dialog.form.isStackable.checkbox.label',
        )}
      </label>
    </div>
  )

  const isOuterPackagingErrorMsg = (
    <div>{getFormErrorMessage('isOuterPackaging')}</div>
  )

  const isOuterPackagingWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.addPackagingItem}
      component={isOuterPackagingComponent}
      label={isOuterPackagingLabel}
      errorMsg={isOuterPackagingErrorMsg}
    />
  )

  // SelectMaster
  const selectMasterLabel = (
    <label
      htmlFor="selectMaster"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('selectMaster'),
      })}
    ></label>
  )

  const valueTemplate = (option: ParentOption, props: DropdownProps) => {
    if (option) {
      return itemTemplate(option)
    }

    return <>{props.placeholder}</>
  }

  const itemTemplate = (option: ParentOption) => {
    return <>{option.name}</>
  }

  const isOuterPackaging = formik.values.isOuterPackaging

  const selectMasterComponent = (
    <Dropdown
      id="selectMaster"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('selectMaster'),
      })}
      value={formik.values.selectMaster}
      onChange={formik.handleChange}
      valueTemplate={valueTemplate}
      itemTemplate={itemTemplate}
      options={parents}
      optionLabel="code"
      optionValue="code"
      disabled={parents.length === 0 || isOuterPackaging}
      showClear
    />
  )

  const selectMasterErrorMsg = <div>{getFormErrorMessage('selectMaster')}</div>

  const { isParentMandatory } = getPackagingItemHierarchyRestrictions(
    formik.values.label,
  )

  const selectMasterWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.addPackagingItem}
      component={selectMasterComponent}
      label={selectMasterLabel}
      errorMsg={selectMasterErrorMsg}
      isRequired={isParentMandatory}
    />
  )

  // Quantity
  const quantityLabel = (
    <label
      htmlFor="quantity"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('quantity'),
      })}
    ></label>
  )

  const quantityComponent = (
    <InputNumber
      id="quantity"
      value={formik.values.quantity}
      onValueChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('quantity'),
      })}
      showButtons
      locale={language}
      min={1}
      disabled={hasBatches()}
    />
  )

  const quantityErrorMsg = <div>{getFormErrorMessage('quantity')}</div>

  const quantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.addPackagingItem}
      component={quantityComponent}
      label={quantityLabel}
      errorMsg={quantityErrorMsg}
    />
  )

  // QuantityUnit
  const quantityUnitLabel = (
    <label
      htmlFor="quantityUnit"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('label'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'quantityUnit',
    config.deliveryForecastDespatchAdvice.addPackagingItem.quantityUnit.options,
    formik,
  )

  const quantityUnitComponent = (
    <Dropdown
      id="quantityUnit"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('quantityUnit'),
      })}
      value={formik.values.quantityUnit}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
    />
  )

  const quantityUnitErrorMsg = <div>{getFormErrorMessage('quantityUnit')}</div>

  const quantityUnitWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.addPackagingItem}
      component={quantityUnitComponent}
      label={quantityUnitLabel}
      errorMsg={quantityUnitErrorMsg}
    />
  )

  // Weight
  const weightLabel = (
    <label
      htmlFor="weight"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('weight'),
      })}
    ></label>
  )

  const weightComponent = (
    <InputNumber
      id="weight"
      value={formik.values.weight}
      onValueChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('weight'),
      })}
      showButtons
      locale={language}
      min={0}
      minFractionDigits={3}
    />
  )

  const weightErrorMsg = <div>{getFormErrorMessage('weight')}</div>

  const weightWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.addPackagingItem}
      component={weightComponent}
      label={weightLabel}
      errorMsg={weightErrorMsg}
    />
  )

  // WeightUnit
  const weightUnitLabel = (
    <label
      htmlFor="weightUnit"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('label'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'weightUnit',
    config.deliveryForecastDespatchAdvice.addPackagingItem.weightUnit.options,
    formik,
  )

  const weightUnitComponent = (
    <Dropdown
      id="weightUnit"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('weightUnit'),
      })}
      value={formik.values.weightUnit}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
    />
  )

  const weightUnitErrorMsg = <div>{getFormErrorMessage('weightUnit')}</div>

  const weightUnitWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.addPackagingItem}
      component={weightUnitComponent}
      label={weightUnitLabel}
      errorMsg={weightUnitErrorMsg}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <div className="flex align-content-center flex-wrap">
      <span className="mr-3">
        {t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.title',
        )}
      </span>
    </div>
  )

  // footer
  const footer = (
    <div className="flex flex-wrap justify-content-between pt-3">
      <Button
        className="p-button-text ml-auto w-full md:w-auto mb-1 md:mb-0"
        label={t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.cancel.label',
        )}
        icon="pi pi-times"
        onClick={handleClose}
        type="button"
      />
      <Button
        label={t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.add.label',
        )}
        className="w-full md:w-auto"
        icon={'pi pi-check'}
        type="submit"
        onClick={formik.submitForm}
        loading={isRequestRunning}
      />
    </div>
  )

  const isMaster = formik.values.label === 'Master'
  const isSingle = formik.values.label === 'Single'
  const isHelper = formik.values.label === 'Helper'

  return (
    <Dialog
      header={header}
      visible={visible}
      style={{ width: '75rem' }}
      breakpoints={{ [calculateBreakpointWidth(75)]: '90vw' }}
      footer={footer}
      onHide={handleClose}
      blockScroll
    >
      <div className="formgrid grid">
        {/* labelWrapper */}
        <div className="col-12 md:col-6 lg:col-4 xl:col-3">{labelWrapper}</div>

        {/* huNumberWrapper */}
        {(isMaster || isSingle) && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {huNumberWrapper}
          </div>
        )}

        {/* isOuterPackagingWrapper */}
        {(isMaster || isSingle) && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {isOuterPackagingWrapper}
          </div>
        )}

        {/* selectMasterWrapper */}
        {(isSingle || isHelper) && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {selectMasterWrapper}
          </div>
        )}

        {/* quantityWrapper */}
        {isSingle && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {quantityWrapper}
          </div>
        )}

        {/* quantityUnitWrapper */}
        {isSingle && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {quantityUnitWrapper}
          </div>
        )}

        {/* weightWrapper */}
        {isSingle && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {weightWrapper}
          </div>
        )}

        {/* weightUnitWrapper */}
        {isSingle && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {weightUnitWrapper}
          </div>
        )}
      </div>

      <Divider className="my-5" />

      {isSingle && (
        <BatchesTableData
          config={config.deliveryForecastDespatchAdvice.addPackagingItem}
          despatchAdviceId={despatchAdviceId}
          itemId={itemId}
          formik={formik}
          isFormFieldValid={isFormFieldValid}
          getFormErrorMessage={getFormErrorMessage}
        >
          <Divider className="my-5" />
        </BatchesTableData>
      )}

      <PackagingMaterialTable
        config={
          config.deliveryForecastDespatchAdvice.addPackagingItem.packagingTable
        }
        formik={formik}
      />
    </Dialog>
  )
}
